import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ItineraryPage.css';
import { getItinerary, COMPANY } from '../data/ItineraryData';
import logo from '../Gallery/logo.png';
import ItineraryPoster from './ItineraryPoster';
import { generatePDF } from '../utils/pdfGenerator';
import { supabase } from '../lib/supabase';

const parseSightseeing = (raw = '') => {
    const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);
    const sections = [];
    let current = null;

    lines.forEach(line => {
        if (line.startsWith('-') || line.startsWith('•')) {
            if (!current) {
                current = { title: 'Sightseeing', items: [] };
                sections.push(current);
            }
            current.items.push(line.replace(/^[-•]\s*/, '').trim());
            return;
        }

        current = { title: line, items: [] };
        sections.push(current);
    });

    return sections.filter(s => s.items.length > 0);
};

const ItineraryPage = () => {
    const { destination } = useParams();
    const navigate = useNavigate();
    const posterRef = useRef(null);

    const [activeDay, setActiveDay] = useState(0);
    const [activeImg, setActiveImg] = useState(0);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const destName = decodeURIComponent(destination);

    useEffect(() => {
        window.scrollTo(0, 0);   // instant reset so bar is never obscured by navbar
        setActiveDay(0);
        setActiveImg(0);
        setLoading(true);

        const fetchItinerary = async () => {
            const slug = destName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            const { data: rows } = await supabase
                .from('itineraries')
                .select('*')
                .or(`slug.eq.${slug},destination.ilike.${destName}`)
                .eq('active', true)
                .limit(1);

            if (rows && rows.length > 0) {
                const row = rows[0];
                const hasOverviewColumns =
                    Object.prototype.hasOwnProperty.call(row, 'overview_accommodation') ||
                    Object.prototype.hasOwnProperty.call(row, 'overview_sightseeing') ||
                    Object.prototype.hasOwnProperty.call(row, 'overview_transport') ||
                    Object.prototype.hasOwnProperty.call(row, 'overview_meals');

                setData({
                    source: 'supabase',
                    hasOverviewColumns,
                    destination: row.destination,
                    region: row.region,
                    tagline: row.tagline,
                    duration: row.duration,
                    priceFrom: row.price_from,
                    hotel: row.hotel,
                    meals: row.meals,
                    bestTime: row.best_time,
                    overview: row.overview,
                    highlights: row.highlights || [],
                    images: row.images && row.images.length > 0
                        ? row.images
                        : ['https://images.unsplash.com/photo-1488085061387-422e29b40080?w=800&auto=format&fit=crop'],
                    inclusions: row.inclusions || [],
                    exclusions: row.exclusions || [],
                    days: row.days || [],
                    overviewAccommodation: row.overview_accommodation || [],
                    overviewSightseeing: row.overview_sightseeing || '',
                    overviewTransport: row.overview_transport || [],
                    overviewMeals: row.overview_meals || [],
                });
            } else {
                // Fallback to static data for destinations not yet added in admin
                setData({
                    source: 'static',
                    hasOverviewColumns: true,
                    ...getItinerary(destName),
                });
            }
            setLoading(false);
        };

        fetchItinerary();
    }, [destination, destName]);

    const handleDownload = async () => {
        if (!posterRef.current || pdfLoading) return;
        try {
            await generatePDF(
                posterRef.current,
                `${data.destination}-Itinerary-SilverSkyHolidays`,
                () => setPdfLoading(true),
                () => setPdfLoading(false),
            );
        } catch {
            setPdfLoading(false);
            alert('PDF generation failed. Please try again.');
        }
    };

    if (loading) {
        return (
            <div className="ip-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center', color: '#64748b' }}>
                    <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
                    <p style={{ fontSize: 14 }}>Loading itinerary...</p>
                </div>
            </div>
        );
    }

    if (!data) return null;

    const fallbackAccommodation = data.hotel
        ? [`${data.duration || 'Trip'} in ${data.destination} at ${data.hotel} or similar`]
        : [];

    const fallbackTransport = (data.inclusions || [])
        .filter(item => /(transfer|flight|transport|airport|coach|vehicle|train|bus)/i.test(item))
        .slice(0, 5);

    const fallbackMeals = data.meals
        ? [data.meals]
        : [];

    const fallbackSightseeingSections = (data.days || []).slice(0, 6).map(day => ({
        title: day.title || `Day ${day.day}`,
        items: (day.activities || []).slice(0, 5),
    })).filter(section => section.items.length > 0);

    const isSupabaseData = data.source === 'supabase';
    const canUseStrictOverview = isSupabaseData && data.hasOverviewColumns;
    const parsedSightseeing = parseSightseeing(data.overviewSightseeing || '');

    const overviewAccommodation = canUseStrictOverview
        ? (data.overviewAccommodation || [])
        : ((data.overviewAccommodation && data.overviewAccommodation.length > 0) ? data.overviewAccommodation : fallbackAccommodation);

    const overviewSightseeingSections = canUseStrictOverview
        ? parsedSightseeing
        : (parsedSightseeing.length > 0 ? parsedSightseeing : fallbackSightseeingSections);

    const overviewTransport = canUseStrictOverview
        ? (data.overviewTransport || [])
        : ((data.overviewTransport && data.overviewTransport.length > 0) ? data.overviewTransport : fallbackTransport);

    const overviewMeals = canUseStrictOverview
        ? (data.overviewMeals || [])
        : ((data.overviewMeals && data.overviewMeals.length > 0) ? data.overviewMeals : fallbackMeals);

    return (
        <div className="ip-page">

            <div style={{ position: 'absolute', top: '-9999px', left: '-9999px', zIndex: -1 }}>
                <ItineraryPoster ref={posterRef} data={data} logoSrc={logo} />
            </div>

            <div className="ip-dl-bar">
                <div className="ip-dl-bar-inner">
                    <button className="ip-back-btn" onClick={() => navigate(-1)}>
                        <i className="fa-solid fa-arrow-left"></i> Back
                    </button>
                    <div className="ip-dl-bar-title">
                        <span className="ip-region-chip">{data.region}</span>
                        <span>{data.destination}</span>
                    </div>
                    <button className={`ip-dl-btn-top ${pdfLoading ? 'loading' : ''}`} onClick={handleDownload} disabled={pdfLoading}>
                        {pdfLoading ? <><span className="ip-spinner" /> Generating…</> : <><i className="fa-solid fa-file-pdf"></i> Download PDF</>}
                    </button>
                </div>
            </div>

            <div className="ip-hero" style={{ backgroundImage: `url(${data.images[0]})` }}>
                <div className="ip-hero-overlay" />
                <div className="ip-hero-content">
                    <span className="ip-region-badge">{data.region}</span>
                    <h1 className="ip-hero-title">{data.destination}</h1>
                    <p className="ip-hero-tagline"><i className="fa-solid fa-plane-departure"></i> {data.tagline}</p>
                    <div className="ip-meta-strip">
                        <div className="ip-meta-card"><i className="fa-solid fa-calendar-days"></i><span>{data.duration}</span></div>
                        <div className="ip-meta-card"><i className="fa-solid fa-hotel"></i><span>{data.hotel}</span></div>
                        <div className="ip-meta-card"><i className="fa-solid fa-utensils"></i><span>{data.meals}</span></div>
                        <div className="ip-meta-card"><i className="fa-solid fa-sun"></i><span>Best: {data.bestTime}</span></div>
                        <div className="ip-meta-card ip-meta-price">
                            <i className="fa-solid fa-tag"></i>
                            <span>From {data.priceFrom} / person</span>
                            <span className="ip-meta-price-varies"><i className="fa-solid fa-calendar-days"></i> Price varies by date</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="ip-content">
                <div className="ip-left">

                    <section className="ip-section">
                        <h2 className="ip-sec-title"><i className="fa-solid fa-earth-asia ip-sec-icon"></i> Trip Overview</h2>
                        <div className="ip-structured-overview">
                            <div className="ip-ov-block ip-ov-accommodation">
                                <h3>Accommodation</h3>
                                {overviewAccommodation.length > 0 ? (
                                    <ul>
                                        {overviewAccommodation.map((item, i) => <li key={i}>{item}</li>)}
                                    </ul>
                                ) : (
                                    <p className="ip-ov-empty"><i className="fa-regular fa-clock" /> Details will be updated soon.</p>
                                )}
                            </div>

                            <div className="ip-ov-grid">
                                <div className="ip-ov-block">
                                    <h3>Sightseeing</h3>
                                    {overviewSightseeingSections.length > 0 ? (
                                        overviewSightseeingSections.map((section, i) => (
                                            <div key={i} className="ip-ov-sight-group">
                                                <h4>{section.title}</h4>
                                                <ul>
                                                    {section.items.map((item, j) => <li key={j}>{item}</li>)}
                                                </ul>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="ip-ov-empty"><i className="fa-regular fa-clock" /> Details will be updated soon.</p>
                                    )}
                                </div>

                                <div className="ip-ov-right-stack">
                                    <div className="ip-ov-block">
                                        <h3>Flight and transport</h3>
                                        {overviewTransport.length > 0 ? (
                                            <ul>
                                                {overviewTransport.map((item, i) => <li key={i}>{item}</li>)}
                                            </ul>
                                        ) : (
                                            <p className="ip-ov-empty"><i className="fa-regular fa-clock" /> Details will be updated soon.</p>
                                        )}
                                    </div>
                                    <div className="ip-ov-block">
                                        <h3>Meals</h3>
                                        {overviewMeals.length > 0 ? (
                                            <ul>
                                                {overviewMeals.map((item, i) => <li key={i}>{item}</li>)}
                                            </ul>
                                        ) : (
                                            <p className="ip-ov-empty"><i className="fa-regular fa-clock" /> Details will be updated soon.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {data.highlights && data.highlights.length > 0 && (
                        <section className="ip-section">
                            <h2 className="ip-sec-title"><i className="fa-solid fa-star ip-sec-icon"></i> Highlights</h2>
                            <div className="ip-highlights">
                                {data.highlights.map((h, i) => (
                                    <div key={i} className="ip-highlight-chip">
                                        <span className="ip-hl-bullet"><i className="fa-solid fa-check"></i></span> {h}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {data.images && data.images.length > 0 && (
                        <section className="ip-section">
                            <h2 className="ip-sec-title"><i className="fa-solid fa-images ip-sec-icon"></i> Gallery</h2>
                            <div className="ip-gallery">
                                <div className="ip-gallery-main">
                                    <img key={activeImg} src={data.images[activeImg]} alt={data.destination} />
                                </div>
                                {data.images.length > 1 && (
                                    <div className="ip-gallery-thumbs">
                                        {data.images.map((img, i) => (
                                            <div key={i} className={`ip-thumb ${activeImg === i ? 'active' : ''}`} onClick={() => setActiveImg(i)}>
                                                <img src={img} alt={`${data.destination} ${i + 1}`} />
                                                {activeImg === i && <div className="ip-thumb-active-ring" />}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {data.days && data.days.length > 0 && (
                        <section className="ip-section">
                            <h2 className="ip-sec-title"><i className="fa-solid fa-calendar-days ip-sec-icon"></i> Day-Wise Itinerary</h2>
                            <div className="ip-day-tabs">
                                {data.days.map((d, i) => (
                                    <button key={i} className={`ip-day-tab ${activeDay === i ? 'active' : ''}`} onClick={() => setActiveDay(i)}>
                                        <span className="ip-day-tab-num">Day {d.day}</span>
                                        <span className="ip-day-tab-icon">{d.icon}</span>
                                    </button>
                                ))}
                            </div>
                            {data.days[activeDay] && (
                                <>
                                    <div className="ip-day-card">
                                        <div className="ip-day-card-header">
                                            <div className="ip-day-badge">Day {data.days[activeDay].day}</div>
                                            <div className="ip-day-icon-big">{data.days[activeDay].icon}</div>
                                            <h3 className="ip-day-title">{data.days[activeDay].title}</h3>
                                        </div>
                                        {data.days[activeDay].description && (
                                            <p className="ip-day-description">{data.days[activeDay].description}</p>
                                        )}
                                        <ul className="ip-day-list">
                                            {(data.days[activeDay].activities || []).map((act, i) => (
                                                <li key={i} className="ip-day-item">
                                                    <span className="ip-day-bullet">→</span>
                                                    <span>{act}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        {data.days[activeDay].included_meals && (
                                            <div className="ip-day-meta-line"><strong>Included Meals :</strong> {data.days[activeDay].included_meals}</div>
                                        )}
                                        {data.days[activeDay].optional_note && (
                                            <div className="ip-day-meta-line"><strong>Optional :</strong> {data.days[activeDay].optional_note}</div>
                                        )}
                                        {data.days[activeDay].notes && (
                                            <div className="ip-day-meta-line"><strong>Notes :</strong> {data.days[activeDay].notes}</div>
                                        )}
                                    </div>
                                    <div className="ip-timeline">
                                        {data.days.map((d, i) => (
                                            <div key={i} className={`ip-timeline-row ${activeDay === i ? 'active' : ''}`} onClick={() => setActiveDay(i)}>
                                                <div className="ip-tl-dot"><span>{d.day}</span></div>
                                                <div className="ip-tl-body">
                                                    <div className="ip-tl-title">{d.icon} {d.title}</div>
                                                    <div className="ip-tl-peek">{d.description || (d.activities || [])[0]}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </section>
                    )}
                </div>

                <aside className="ip-sidebar">
                    <div className="ip-price-card">
                        <div className="ip-price-varies-badge">
                            <i className="fa-solid fa-circle-info"></i>
                            <span>Price may vary based on travel date &amp; season</span>
                        </div>
                        <div className="ip-price-card-header">
                            <span className="ip-price-label">Starting From</span>
                            <span className="ip-price-value">{data.priceFrom}</span>
                            <span className="ip-price-per">per person</span>
                        </div>
                        <button className="ip-enquire-btn"><i className="fa-solid fa-phone"></i> Enquire Now</button>
                        <button className={`ip-dl-btn ${pdfLoading ? 'loading' : ''}`} onClick={handleDownload} disabled={pdfLoading}>
                            {pdfLoading ? '⏳ Generating…' : <><i className="fa-solid fa-file-pdf"></i> Download PDF</>}
                        </button>
                        <div className="ip-price-contact">
                            <div><i className="fa-solid fa-phone"></i> {COMPANY.phone}</div>
                            <div><i className="fa-solid fa-phone"></i> {COMPANY.altPhone}</div>
                            <div><i className="fa-solid fa-envelope"></i> {COMPANY.email}</div>
                            <div><i className="fa-solid fa-globe"></i> {COMPANY.website}</div>
                        </div>
                    </div>

                    {data.inclusions && data.inclusions.length > 0 && (
                        <div className="ip-sidebar-card ip-inc-card">
                            <h3><i className="fa-solid fa-circle-check"></i> What's Included</h3>
                            <ul>{data.inclusions.map((item, i) => <li key={i}><span className="ip-inc-dot">✔</span>{item}</li>)}</ul>
                        </div>
                    )}

                    {data.exclusions && data.exclusions.length > 0 && (
                        <div className="ip-sidebar-card ip-exc-card">
                            <h3><i className="fa-solid fa-circle-xmark"></i> Not Included</h3>
                            <ul>{data.exclusions.map((item, i) => <li key={i}><span className="ip-exc-dot">✘</span>{item}</li>)}</ul>
                        </div>
                    )}

                    <div className="ip-company-card">
                        <img src={logo} alt="Silver Sky Holidays" className="ip-company-logo" />
                        <div className="ip-company-name">{COMPANY.name}</div>
                        <div className="ip-company-name-bar">
                            <span /><span /><span /><span />
                        </div>
                        <div className="ip-company-tag">{COMPANY.tagline}</div>
                        <div className="ip-company-cert">
                            <span><i className="fa-solid fa-calendar-check"></i> Est. 1995 · 30+ Yrs</span>
                            <span><i className="fa-solid fa-users"></i> 15,000+ Families</span>
                        </div>
                        <div className="ip-company-addr"><i className="fa-solid fa-location-dot"></i> {COMPANY.address}</div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default ItineraryPage;
